import { BrainCircuit, ChevronUp, Send, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { UseChatStreamResult } from "../../lib/use-chat-stream";
import { useFocusTrap } from "../../lib/use-focus-trap";
import { cn } from "../../lib/utils";
import { ChatMessage } from "./ChatMessage";
import { ToolCallChip } from "./ToolCallChip";

function ThinkingBubble() {
  return (
    <div className="flex gap-2.5 px-4 py-2 items-start">
      <span
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-indigo-500"
        aria-hidden="true"
      >
        <BrainCircuit className="h-3.5 w-3.5 text-white" />
      </span>
      <div className="flex items-center gap-1.5 rounded-lg bg-surface-muted px-3.5 py-3">
        {[0, 160, 320].map((delay) => (
          <span
            key={delay}
            className="animate-thinking-dot h-2 w-2 rounded-full bg-violet-400"
            style={{ animationDelay: `${delay}ms` }}
            aria-hidden="true"
          />
        ))}
      </div>
      <span className="sr-only">Thinking…</span>
    </div>
  );
}

interface ChatDrawerProps {
  readonly isOpen: boolean;
  readonly onOpen: () => void;
  readonly onMinimize: () => void;
  readonly onClose: () => void;
  readonly chat: UseChatStreamResult;
}

const EXAMPLE_PROMPTS = [
  "How did we do today?",
  "Find recent cash payments",
  "Look up order #1234",
];

export function ChatDrawer({ isOpen, onOpen, onMinimize, onClose, chat }: ChatDrawerProps) {
  const { t, i18n } = useTranslation();
  const panelRef = useFocusTrap<HTMLElement>(isOpen, onMinimize);
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { messages, pendingText, activeTools, streaming, error, send, cancel } = chat;

  const handleSend = () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    send(text, i18n.resolvedLanguage ?? "en");
  };

  useEffect(() => {
    if (!streaming && isOpen) {
      textareaRef.current?.focus();
    }
  }, [streaming, isOpen]);

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    if (isOpen) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isOpen, messages, pendingText, activeTools]);

  useEffect(() => {
    if (!isOpen) return;
    // useFocusTrap fires a setTimeout(0) to focus the first element (the
    // header button). We wait 50ms to win that race and land on the textarea.
    const t = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  const isEmpty = messages.length === 0 && !streaming && !error;
  const hasActivity = messages.length > 0 || streaming;

  // ── Minimized pill ──────────────────────────────────────────────────────── //
  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2">
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            "flex items-center gap-2 rounded-full px-4 py-2.5 shadow-elev-3 transition-all",
            "bg-gradient-to-r from-violet-600 to-indigo-500 text-white",
            "hover:shadow-elev-2 hover:opacity-90",
          )}
          aria-label={t("ai.chat.title")}
        >
          <BrainCircuit className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="text-xs font-semibold">{t("ai.chat.title")}</span>
          {hasActivity && (
            <span className="flex h-1.5 w-1.5 rounded-full bg-white/80" aria-hidden="true" />
          )}
          <ChevronUp className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-[0.625rem] text-muted underline-offset-2 hover:underline"
          aria-label={t("ai.chat.dismiss")}
        >
          {t("ai.chat.dismiss")}
        </button>
      </div>
    );
  }

  // ── Full drawer ─────────────────────────────────────────────────────────── //
  return (
    <div
      className="fixed inset-x-0 bottom-0 top-14 z-40"
      role="dialog"
      aria-modal="true"
      aria-label={t("ai.chat.title")}
    >
      {/* Backdrop */}
      <div
        data-testid="chat-backdrop"
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onMinimize}
      />

      {/* Drawer panel */}
      <aside
        ref={panelRef}
        className={cn(
          "absolute bottom-0 right-0 top-0 flex w-full max-w-md flex-col bg-surface shadow-elev-3",
          "animate-rise-in motion-reduce:animate-none",
        )}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between bg-gradient-to-r from-violet-600 to-indigo-500 px-4 py-3">
          <div className="flex items-center gap-2">
            <BrainCircuit className="h-4 w-4 text-white/90" aria-hidden="true" />
            <h2 className="text-sm font-bold tracking-wide text-white">{t("ai.chat.title")}</h2>
          </div>
          <div className="flex items-center gap-1">
            {/* Minimise - keeps history */}
            <button
              type="button"
              onClick={onMinimize}
              className="rounded-md p-1 text-white/70 hover:bg-white/20 hover:text-white"
              aria-label={t("ai.chat.minimize")}
            >
              <ChevronUp className="h-4 w-4 rotate-180" aria-hidden="true" />
            </button>
            {/* Close - hides the pill entirely */}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-white/70 hover:bg-white/20 hover:text-white"
              aria-label={t("common.close")}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Message list */}
        <div className="min-h-0 flex-1 overflow-y-auto py-2">
          {isEmpty && (
            <div className="flex flex-col items-center gap-4 px-6 py-8 text-center">
              <BrainCircuit className="h-10 w-10 text-muted/50" aria-hidden="true" />
              <p className="text-xs text-muted">{t("ai.chat.emptyHint")}</p>
              <div className="flex flex-col gap-2 w-full">
                {EXAMPLE_PROMPTS.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => {
                      setInput("");
                      send(ex, i18n.resolvedLanguage ?? "en");
                    }}
                    className="rounded-lg border border-line bg-surface-muted px-3 py-2 text-left text-xs text-strong transition hover:bg-surface hover:shadow-elev-1"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <ChatMessage key={i} msg={msg} />
          ))}

          {activeTools.map((name, i) => (
            <ToolCallChip key={`${name}-${i}`} name={name} />
          ))}

          {streaming && !pendingText && activeTools.length === 0 && <ThinkingBubble />}

          {pendingText && (
            <div className="flex gap-2.5 px-4 py-2 items-start">
              <span
                className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-indigo-500"
                aria-hidden="true"
              >
                <BrainCircuit className="h-3.5 w-3.5 text-white" />
              </span>
              <div className="max-w-[calc(100%-2.5rem)] rounded-lg bg-surface-muted px-3 py-2 text-xs leading-relaxed text-strong">
                {pendingText}
                <span
                  className="ml-0.5 inline-block h-3 w-0.5 animate-pulse bg-current align-middle"
                  aria-hidden="true"
                />
              </div>
            </div>
          )}

          {!streaming && !pendingText && messages.length > 0 && activeTools.length === 0 && (
            <div aria-live="polite" className="sr-only">
              {t("ai.chat.responseDone")}
            </div>
          )}

          {error && (
            <div className="mx-4 my-2 rounded-lg border border-line bg-surface-muted px-3 py-2 text-xs text-muted">
              {error === "cost_cap_exceeded" ? t("ai.chat.errorCap") : t("ai.chat.errorGeneric")}
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div className="shrink-0 border-t border-line p-3">
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={t("ai.chat.placeholder")}
              rows={1}
              className={cn(
                "min-h-tap flex-1 resize-none rounded-lg border border-line bg-surface-muted px-3 py-2",
                "text-xs text-strong placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-primary/50",
                "max-h-24 overflow-y-auto",
              )}
              aria-label={t("ai.chat.placeholder")}
            />
            {streaming ? (
              <button
                type="button"
                onClick={cancel}
                className="flex min-h-tap w-9 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-muted transition hover:bg-surface hover:text-strong"
                aria-label={t("ai.chat.stop")}
              >
                <span className="h-3 w-3 rounded-sm bg-current" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSend}
                disabled={!input.trim()}
                className={cn(
                  "flex min-h-tap w-9 shrink-0 items-center justify-center rounded-lg transition",
                  input.trim()
                    ? "bg-primary text-primary-foreground hover:opacity-90"
                    : "bg-surface-muted text-muted cursor-not-allowed",
                )}
                aria-label={t("ai.chat.send")}
              >
                <Send className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
